import { SessionSelectors } from '@next/core-lib/session';
import { SelectedCustomer, UserSelectors } from '@next/core-lib/user';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import { BlightData, ParticipatingCropField } from '../blight-v2/models/models';
import { AppState, ArableSessionState, GroupedSprays, SprayApplicationWithIndex } from '../shared/models';

export const selectArableSession = createSelector(
  SessionSelectors.selectSessionState,
  (session): ArableSessionState => (session ?? {}) as ArableSessionState,
);

export const selectCurrentCropYear = createSelector(
  () => new Date().getFullYear(),
  (currentYear) => currentYear,
);

export const getAvailableCropYears = (currentYear = new Date().getFullYear()): number[] =>
  Array.from({ length: 5 }, (_, index) => currentYear - 3 + index);

export const normalizeCropYear = (
  cropYear: number | null | undefined,
  currentYear = new Date().getFullYear(),
): number => (typeof cropYear === 'number' && getAvailableCropYears(currentYear).includes(cropYear) ? cropYear : currentYear);

export const selectCropYear = createSelector(
  selectArableSession,
  selectCurrentCropYear,
  (session, currentYear) => normalizeCropYear(session.arable?.cropYear, currentYear),
);

export const selectAvailableCropYears = createSelector(selectCurrentCropYear, (currentYear) =>
  getAvailableCropYears(currentYear),
);

export const selectIsCurrentCropYear = createSelector(
  selectCropYear,
  selectCurrentCropYear,
  (cropYear, currentYear) => cropYear === currentYear,
);

export const isCropYearReadOnly = (cropYear: number, currentYear: number): boolean => cropYear !== currentYear;

export const selectIsCropYearReadOnly = createSelector(
  selectCropYear,
  selectCurrentCropYear,
  isCropYearReadOnly,
);

export const selectApp = createFeatureSelector<AppState>('app');

export const selectBlightData = createSelector(selectApp, (store: AppState) => store.blightData);
export const selectBlightParticipatingCropFields = createSelector(selectApp, (store: AppState) => store.blightParticipatingCropFields);

export const selectBlightCropFields =
  createSelector(selectBlightData, selectBlightParticipatingCropFields, (blightData, cropFields) => cropFields
    .filter((cropField) => excludeNonBlightCropFields(blightData, cropField))
    .map((cf) => ({ ...cf, isVisible: blightData.find((bd) => bd.cropFieldId === cf.id)?.isCropFieldVisible }))
    .sort((first, second) => first.name.localeCompare(second.name)));

export const selectFilteredBlightCropFields = createSelector(selectBlightCropFields, (blightCropFields) => blightCropFields.filter((bcf) => bcf.isVisible));

export const selectBlightChartDetails = createSelector(selectApp, (store: AppState) => store.blightChartDetails);
export const selectNewBlightDataForCropField = createSelector(selectApp, (store: AppState) => store.newBlightDataForCropField);

export const selectCustomerBlightRights = createSelector(
  selectApp,
  UserSelectors.selectCustomer,
  (store: AppState, customer: SelectedCustomer): { isPremium: boolean } => {
    if (!customer || !store.customerBlightRights) {
      return { isPremium: false };
    }
    return { isPremium: store.customerBlightRights.customerId === customer.id && store.customerBlightRights.isPremium };
  },
);

export const selectBasicSprays = createSelector(selectApp, (store: AppState) => store.basicSprays);
export const selectSprayApplications = createSelector(selectApp, (store: AppState) => store.sprayApplications);
export const selectIsSpraysLoading = createSelector(selectApp, (store: AppState) => store.isSpraysLoading);
export const selectCropFieldRegistration = createSelector(selectApp, (store: AppState) => store.cropFieldRegistration);
export const selectCropFieldPlantDate = createSelector(selectApp, (store: AppState) => store.cropFieldRegistration?.plantDate);
export const selectCropFieldEmergenceDate = createSelector(selectApp, (store: AppState) => store.cropFieldRegistration?.emergenceDate);
export const selectShouldRecalculateForCropFieldIds = createSelector(selectApp, (store: AppState) => store.shouldRecalculateForCropFieldIds);
export const selectShowRecalculationFinishedNotification = createSelector(selectApp, (store: AppState) => store.showRecalculationFinishedNotification);
export const selectSelectedCropField = createSelector(selectApp, (store: AppState) => store.selectedCropField);
export const selectSelectedBlightCropFieldIds = createSelector(selectApp, (store: AppState) => store.selectedBlightCropFieldIds);

export const selectSelectedBlightCropFields = createSelector(selectApp, (store: AppState) =>
  store.blightParticipatingCropFields.filter((cf) => store.selectedBlightCropFieldIds.includes(cf.id)),
);

export const selectCropFieldsInRecalculation = createSelector(selectApp, (store: AppState) =>
  store.blightRecalculationStatusPerCropField
    .filter((x) => x.isBlightRecalculating)
    .map((status) => ({
      id: status.cropFieldId,
      name: store.blightParticipatingCropFields.find((cropField) => cropField.id === status.cropFieldId)?.name,
    })),
);

export const selectBlightRecalculationStatusPerCropFieldById = (cropFieldId: number) =>
  createSelector(
    selectApp,
    (store: AppState) => store.blightRecalculationStatusPerCropField.find((status) => status.cropFieldId === cropFieldId)?.isBlightRecalculating,
  );

export const selectBlightAdviceDate = createSelector(selectApp, (store: AppState) => store.blightAdviceDate);

export const selectLegalRestrictions = createSelector(selectApp, (state) => state?.legalRestrictions || []);

export const selectShouldGetNewBlightData = createSelector(selectApp, (store: AppState): boolean => store?.shouldGetNewBlightData);

export const selectGroupedSpraysBySelectedCropFields = createSelector(
  selectSprayApplications,
  selectSelectedBlightCropFieldIds,
  selectSelectedBlightCropFields,
  (applications, selectedIds, cropFields): GroupedSprays[] => {
    if (!applications?.length || !selectedIds?.length || !cropFields?.length) {
      return [];
    }

    const cropFieldsMap = new Map(cropFields.map((field) => [field.id, field.name]));

    return Object.values(
      applications
        .filter((spray) => selectedIds.includes(spray.cropFieldId))
        .reduce((acc, curr) => {
          if (!acc[curr.cropFieldId]) {
            acc[curr.cropFieldId] = {
              cropFieldId: curr.cropFieldId,
              cropFieldName: cropFieldsMap.get(curr.cropFieldId) || 'Unknown',
              sprays: [],
            };
          }
          const sprayWithIndex: SprayApplicationWithIndex = {
            ...curr,
            sprayIndex: 0,
          };

          acc[curr.cropFieldId].sprays.push(sprayWithIndex);
          return acc;
        }, {} as { [key: number]: GroupedSprays }),
    ).map((group) => {
      const sortedSprays = [...group.sprays]
        .sort((a, b) => new Date(b.sprayedOn).getTime() - new Date(a.sprayedOn).getTime())
        .map((spray, index) => ({
          ...spray,
          sprayIndex: group.sprays.length - index,
        }));
      return {
        ...group,
        sprays: sortedSprays,
      };
    });
  },
);

export const selectCropFieldPlantDateConfirmed =
  createSelector(
    selectApp,
    (store: AppState) => store.cropFieldRegistration?.isPlantDateConfirmed ?? false,
  );

export const selectIsAnySelectedCropFieldRecalculating = createSelector(
  selectCropFieldsInRecalculation,
  selectSelectedBlightCropFieldIds,
  (calculatingCropFields, selectedCropFieldIds): boolean => calculatingCropFields.some((calcField) => selectedCropFieldIds.includes(calcField.id)),
);

const excludeNonBlightCropFields = (blightData: BlightData[], cropField: ParticipatingCropField) => {
  const cropFieldIds = blightData.map((bd) => bd.cropFieldId);
  return cropFieldIds.includes(cropField.id);
};
